/**
 * Admin categories API module.
 *
 * Endpoints covered:
 *   GET    /api/v1/admin/categories          — paginated flat list (or tree with ?as=tree)
 *   GET    /api/v1/admin/categories/:id      — single category
 *   POST   /api/v1/admin/categories          — create
 *   PATCH  /api/v1/admin/categories/:id      — update (partial)
 *   DELETE /api/v1/admin/categories/:id      — soft-delete
 *
 * Snake→camel projection for pagination lives here, not in the slice.
 * Per memory `project_snake_camel_wire_shape_gap`, the platform emits
 * `meta.pagination` with snake_case keys; this module maps them to the
 * camelCase `AdminCategoryListResponse` shape the slice consumes.
 */
import { apiClient } from '@dfq/shared/api/client';
import { throwAsApiError, unwrap } from '@dfq/shared/api/_envelope';
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  AdminCategoryCreatePayload,
  AdminCategoryDTO,
  AdminCategoryListParams,
  AdminCategoryListResponse,
  AdminCategoryUpdatePayload,
} from '@dfq/shared/types/category';

/** Snake_case pagination emitted by `meta.pagination` in the platform envelope. */
interface WireCategoryPaginationMeta {
  page: number;
  per_page: number;
  total: number;
  last_page: number;
  has_more: boolean;
}

export async function fetchAdminCategories(
  params: AdminCategoryListParams = {},
): Promise<AdminCategoryListResponse> {
  try {
    const queryParams: Record<string, string | number | boolean | undefined> = {
      page: params.page,
      per_page: params.per_page,
      sort: params.sort,
      direction: params.direction,
      search: params.search,
      is_visible: params.is_visible,
      as: params.as,
    };

    if (params.parent_id !== undefined) {
      (queryParams as Record<string, unknown>).parent_id = params.parent_id;
    }

    const res = await apiClient.get<
      ApiEnvelope<{ categories: AdminCategoryDTO[] }> & {
        meta: { pagination?: WireCategoryPaginationMeta };
      }
    >('/admin/categories', { params: queryParams });

    const data = unwrap(res.data, res.status);
    const pagination = res.data.meta?.pagination;

    return {
      categories: data.categories,
      total: pagination?.total ?? 0,
      page: pagination?.page ?? 1,
      perPage: pagination?.per_page ?? 25,
      lastPage: pagination?.last_page ?? 1,
    } satisfies AdminCategoryListResponse;
  } catch (err) {
    throwAsApiError(err);
  }
}

export async function fetchAdminCategoryTree(
  rootId?: number | null,
): Promise<AdminCategoryDTO[]> {
  try {
    const queryParams: Record<string, unknown> = { as: 'tree' };
    if (rootId != null) queryParams.parent_id = rootId;

    const res = await apiClient.get<ApiEnvelope<{ categories: AdminCategoryDTO[] }>>(
      '/admin/categories',
      { params: queryParams },
    );
    return unwrap(res.data, res.status).categories;
  } catch (err) {
    throwAsApiError(err);
  }
}

export async function fetchAdminCategory(id: number): Promise<AdminCategoryDTO> {
  try {
    const res = await apiClient.get<ApiEnvelope<{ category: AdminCategoryDTO }>>(
      `/admin/categories/${id}`,
    );
    return unwrap(res.data, res.status).category;
  } catch (err) {
    throwAsApiError(err);
  }
}

export async function createAdminCategory(
  payload: AdminCategoryCreatePayload,
): Promise<AdminCategoryDTO> {
  try {
    const res = await apiClient.post<ApiEnvelope<{ category: AdminCategoryDTO }>>(
      '/admin/categories',
      payload,
    );
    return unwrap(res.data, res.status).category;
  } catch (err) {
    throwAsApiError(err);
  }
}

export async function updateAdminCategory(
  id: number,
  payload: AdminCategoryUpdatePayload,
): Promise<AdminCategoryDTO> {
  try {
    const res = await apiClient.patch<ApiEnvelope<{ category: AdminCategoryDTO }>>(
      `/admin/categories/${id}`,
      payload,
    );
    return unwrap(res.data, res.status).category;
  } catch (err) {
    throwAsApiError(err);
  }
}

export async function deleteAdminCategory(id: number): Promise<void> {
  try {
    await apiClient.delete(`/admin/categories/${id}`);
  } catch (err) {
    throwAsApiError(err);
  }
}
